<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="executive-source-slope"
    />
    <ChartContent
      height="261px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="slopeChart"
      :number-of-lines="5"
      class="px-0 pl-3"
      :class="{ 'px-3': loading }"
      :link="{ name: 'Source Report', to: 'overall-report' }"
    >
      <LineChart :id="widgetId" :data="chartData" :options="chartOptions" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import LineChart from '@/components/insights/Chart/LineChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import { executiveInsightsPluginsConfig, executiveInsightsYAxisOptions, executiveInsightsXScaleOptions } from '@/api/helpers/insights/commonChartConfigs';

const MAX_VALUE = 100;

const props = defineProps({
  sourceDistribution: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What are our best sources?';

const widgetId = 'source-distribution';

const {
  sourceInbound, sourceOutbound, sourceInternal, sourceOther, sourceReferral, white,
} = useColors();

const noWidgetData = computed(() => Object.values(props.sourceDistribution || {})
  .every((value) => !value));

const chartData = computed(() => {
  const {
    inboundAppsPercent,
    inboundHiredPercent,
    outboundAppsPercent,
    outboundHiredPercent,
    referralAppsPercent,
    referralHiredPercent,
    internalAppsPercent,
    internalHiredPercent,
    otherAppsPercent,
    otherHiredPercent,
  } = props.sourceDistribution || {};

  const datasets = [
    {
      data: [inboundAppsPercent, inboundHiredPercent],
      label: 'Inbound',
      borderColor: sourceInbound.value,
      backgroundColor: sourceInbound.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
    {
      data: [outboundAppsPercent, outboundHiredPercent],
      label: 'Outbound',
      borderColor: sourceOutbound.value,
      backgroundColor: sourceOutbound.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
    {
      data: [referralAppsPercent, referralHiredPercent],
      label: 'Referral',
      borderColor: sourceReferral.value,
      backgroundColor: sourceReferral.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
    {
      data: [internalAppsPercent, internalHiredPercent],
      label: 'Internal',
      borderColor: sourceInternal.value,
      backgroundColor: sourceInternal.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
    {
      data: [otherAppsPercent, otherHiredPercent],
      label: 'Other',
      borderColor: sourceOther.value,
      backgroundColor: sourceOther.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
  ];
  return {
    labels: ['Application', '            Hire'],
    datasets,
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  layout: {
    padding: {
      bottom: 16,
    },
  },
  plugins: executiveInsightsPluginsConfig(chartData, widgetId),
  scales: {
    y: executiveInsightsYAxisOptions(MAX_VALUE),
    y2: {
      position: 'right',
      ...executiveInsightsYAxisOptions(MAX_VALUE),
    },
    x: executiveInsightsXScaleOptions,
  },
}));
</script>

<style lang="less" scoped>
:deep(.chart-error-image) {
  height: 261px !important;
  max-height: 261px !important;
  padding: 50px !important;
  margin: 0 !important;
}
</style>
